import React, { useCallback, useState } from 'react';
import { Button, ButtonProps, Form, Modal } from 'react-bootstrap';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { faPlug } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import useLocalStore from 'src/store/useLocalStore';
import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CustomModal from 'src/components/custom_components/CustomModal/CustomModal';

import CameraConnection from 'src/models/Camera/CameraConnection';
import { I18NextNS } from 'src/models/Language.model';

import { captureError } from 'src/utils/errorReporting';

export type ManualConnectCameraProps = {
  isScanning: boolean;
} & ButtonProps;

export default function ManualConnectCamera({
  isScanning,
  ...props
}: ManualConnectCameraProps) {
  const { t } = useTranslation(I18NextNS.PAGES);

  const schema = z.object({
    ip: z.string().ip({
      message: t('settings.cameraModes.cameraConnection.invalidIpAddress'),
    }),
  });

  const { setCameraData } = useRemoteBoundStore();
  const { setCameraIP } = useLocalStore();

  const [isOpenInputIPModal, setIsOpenInputIPModal] = useState(false);
  const [cameraNotFoundMessage, setCameraNotFoundMessage] = useState('');

  const handleCloseInputIPModal = () => {
    setIsOpenInputIPModal(false);
    reset();
    setCameraNotFoundMessage('');
  };

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      ip: '',
    },
    resolver: zodResolver(schema),
  });

  const handleConnectCamera = useCallback(
    async (ip: string) => {
      try {
        const cameraData = await CameraConnection.loadCameraData(
          ip,
          t('message.error.cameraIpMissingMsg', { ns: I18NextNS.COMMON }),
        );

        await CameraConnection.getInstance().say(
          'Camera successfully connected.',
        );

        setCameraData(cameraData);
        setCameraIP(ip);
      } catch (error) {
        captureError(error, 'Camera has not been found');

        setCameraNotFoundMessage(
          t('message.error.cameraNotFound', { ns: I18NextNS.COMMON }),
        );
      }
    },
    [setCameraData, t, setCameraIP],
  );

  const onSubmit: SubmitHandler<{
    ip: string;
  }> = async (data) => {
    await handleConnectCamera(data.ip);
  };

  return (
    <>
      <Button
        variant="dark"
        onClick={() => setIsOpenInputIPModal(true)}
        disabled={isScanning}
        {...props}
      >
        <FontAwesomeIcon
          icon={faPlug}
          className="me-2"
          data-test="settings-manual-connect-button"
        />
        {t('settings.cameraModes.cameraConnection.manualConnect')}
      </Button>

      <CustomModal show={isOpenInputIPModal} onHide={handleCloseInputIPModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {t('settings.cameraModes.cameraConnection.connectToDevice')}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="ip">
              <Form.Label>
                {t('settings.cameraModes.cameraConnection.ipAddress')}:
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={t(
                  'settings.cameraModes.cameraConnection.ipAddressPlaceholder',
                )}
                {...register('ip')}
                onInput={() => setCameraNotFoundMessage('')}
              />
              {cameraNotFoundMessage && (
                <p className="text-danger">{cameraNotFoundMessage}</p>
              )}
              {errors.ip?.message && (
                <p className="text-danger">{errors.ip?.message}</p>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary-100" onClick={handleCloseInputIPModal}>
              {t('settings.cameraModes.cameraConnection.close')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitting}
              onClick={() => setCameraNotFoundMessage('')}
              data-test="connect-camera"
            >
              {isSubmitting
                ? `${t('settings.cameraModes.cameraConnection.connecting')}...`
                : `${t('settings.cameraModes.cameraConnection.connect')}`}
            </Button>
          </Modal.Footer>
        </Form>
      </CustomModal>
    </>
  );
}
